/**
 * Vitolier platform sharing — canonical content registry.
 *
 * One place defines every shareable content type, its canonical public route
 * and whether a safe public route exists at all. Adding a new content type is
 * a single entry here; no new sharing implementation is ever required.
 *
 * Canonical URLs are always locale-prefixed public routes. Temporary, signed,
 * preview, edit, admin and dashboard URLs are never shareable.
 */

export const SHARE_ENTITY_TYPES = [
  "brand",
  "cigar",
  "cigar_line",
  "event",
  "lounge",
  "retailer",
  "club",
  "association",
  "manufacturer",
  "distributor",
  "atlas_collection",
  "culture_article",
  "news_article",
  "cigar101_guide",
  "cigar101_term",
  "learn_article",
  "social_post",
  "review",
  "smoking_session",
  "collection",
  "member_profile",
  "trade_announcement",
] as const;

export type ShareEntityType = (typeof SHARE_ENTITY_TYPES)[number];

export type ShareOgType = "website" | "article" | "profile";

export interface ShareRouteParams {
  /** Preferred public identifier (slug, username). */
  slug?: string | null;
  /** Parent slug, used by nested routes such as brand → cigar line. */
  parentSlug?: string | null;
  /** Sub-type used by the Atlas place route (lounge, retailer, …). */
  placeType?: string | null;
  /** Last-resort identifier when a type has no slug (e.g. community posts). */
  id?: string | null;
}

export interface ShareEntityConfig {
  label: string;
  ogType: ShareOgType;
  /** False when Vitolier has no safe public route for this type yet. */
  hasPublicRoute: boolean;
  /** Build the locale-free public path, or null when it cannot be resolved. */
  buildPath: (params: ShareRouteParams) => string | null;
}

const bySlug = (prefix: string) => (p: ShareRouteParams) =>
  p.slug ? `${prefix}/${p.slug}` : null;

export const SHARE_ENTITY_REGISTRY: Record<ShareEntityType, ShareEntityConfig> = {
  brand: { label: "Brand", ogType: "profile", hasPublicRoute: true, buildPath: bySlug("/brands") },
  cigar: { label: "Cigar", ogType: "article", hasPublicRoute: true, buildPath: bySlug("/cigars") },
  cigar_line: {
    label: "Cigar Line",
    ogType: "article",
    hasPublicRoute: true,
    buildPath: (p) => (p.parentSlug && p.slug ? `/brands/${p.parentSlug}/lines/${p.slug}` : null),
  },
  event: { label: "Event", ogType: "article", hasPublicRoute: true, buildPath: bySlug("/events") },
  lounge: {
    label: "Lounge",
    ogType: "profile",
    hasPublicRoute: true,
    buildPath: (p) => (p.slug ? `/cigar-atlas/lounge/${p.slug}` : null),
  },
  retailer: {
    label: "Retailer",
    ogType: "profile",
    hasPublicRoute: true,
    buildPath: (p) => (p.slug ? `/cigar-atlas/retailer/${p.slug}` : null),
  },
  club: { label: "Club", ogType: "profile", hasPublicRoute: true, buildPath: bySlug("/clubs") },
  association: {
    label: "Association",
    ogType: "profile",
    hasPublicRoute: true,
    buildPath: (p) => (p.slug ? `/cigar-atlas/association/${p.slug}` : null),
  },
  manufacturer: {
    label: "Manufacturer",
    ogType: "profile",
    hasPublicRoute: true,
    buildPath: (p) => (p.slug ? `/cigar-atlas/manufacturer/${p.slug}` : null),
  },
  distributor: {
    label: "Distributor",
    ogType: "profile",
    hasPublicRoute: true,
    buildPath: (p) => (p.slug ? `/cigar-atlas/distributor/${p.slug}` : null),
  },
  atlas_collection: {
    label: "Atlas collection",
    ogType: "website",
    hasPublicRoute: true,
    buildPath: (p) => (p.slug ? `/cigar-atlas/collection/${p.slug}` : null),
  },
  culture_article: {
    label: "Culture article",
    ogType: "article",
    hasPublicRoute: true,
    buildPath: bySlug("/culture"),
  },
  news_article: { label: "News", ogType: "article", hasPublicRoute: true, buildPath: bySlug("/news") },
  cigar101_guide: {
    label: "Cigar 101 guide",
    ogType: "article",
    hasPublicRoute: true,
    buildPath: (p) => (p.slug ? `/cigar-101/guides/${p.slug}` : null),
  },
  cigar101_term: {
    label: "Cigar 101 term",
    ogType: "article",
    hasPublicRoute: true,
    buildPath: (p) => (p.slug ? `/cigar-101/glossary/${p.slug}` : null),
  },
  learn_article: {
    label: "Learn article",
    ogType: "article",
    hasPublicRoute: true,
    buildPath: (p) => (p.slug ? `/learn/${p.slug}` : "/learn"),
  },
  social_post: {
    label: "Post",
    ogType: "article",
    hasPublicRoute: true,
    // Community posts are addressed by their public message identifier.
    buildPath: (p) => (p.id ? `/post/${p.id}` : null),
  },
  member_profile: {
    label: "Member profile",
    ogType: "profile",
    hasPublicRoute: true,
    buildPath: (p) => (p.slug ? `/members/${p.slug}` : null),
  },
  // Types below have no verified public route yet — recorded, never shareable.
  review: { label: "Review", ogType: "article", hasPublicRoute: false, buildPath: () => null },
  smoking_session: {
    label: "Smoking session",
    ogType: "article",
    hasPublicRoute: false,
    buildPath: () => null,
  },
  collection: { label: "Collection", ogType: "website", hasPublicRoute: false, buildPath: () => null },
  trade_announcement: {
    label: "Trade announcement",
    ogType: "article",
    hasPublicRoute: false,
    buildPath: () => null,
  },
};

export const DEFAULT_SHARE_ORIGIN = "https://vitolier.com";

/** Paths that must never be shared externally. */
const INTERNAL_PATH = /^\/(admin|trade\/manage|preview|auth|share\/join)(\/|$)/;

export function isShareablePath(path: string | null | undefined): boolean {
  if (!path || !path.startsWith("/")) return false;
  return !INTERNAL_PATH.test(path);
}

function currentOrigin(): string {
  if (typeof window !== "undefined" && window.location?.origin) return window.location.origin;
  return DEFAULT_SHARE_ORIGIN;
}

export interface CanonicalUrlInput {
  entityType: ShareEntityType;
  params?: ShareRouteParams;
  /** Explicit locale-free path, overriding the registry route. */
  path?: string | null;
  locale?: string | null;
  origin?: string;
}

/** Locale-free canonical path, or null when no safe public route exists. */
export function buildCanonicalPath(input: CanonicalUrlInput): string | null {
  const config = SHARE_ENTITY_REGISTRY[input.entityType];
  if (!config) return null;
  const raw = input.path ?? config.buildPath(input.params ?? {});
  if (!raw) return null;
  const path = raw.startsWith("/") ? raw : `/${raw}`;
  if (!isShareablePath(path)) return null;
  if (!input.path && !config.hasPublicRoute) return null;
  return path;
}

/** Absolute canonical URL, locale-prefixed, with no tracking parameters. */
export function buildCanonicalUrl(input: CanonicalUrlInput): string | null {
  const path = buildCanonicalPath(input);
  if (!path) return null;
  const origin = input.origin ?? currentOrigin();
  const locale = input.locale ? `/${input.locale}` : "";
  return `${origin}${locale}${path}`;
}

/** First-party attribution parameter name. Opaque token, no identifiers. */
export const SHARE_TOKEN_PARAM = "s";

/** Append the opaque attribution token to a canonical URL. */
export function withShareToken(url: string, token: string | null | undefined): string {
  if (!token) return url;
  const joiner = url.includes("?") ? "&" : "?";
  return `${url}${joiner}${SHARE_TOKEN_PARAM}=${encodeURIComponent(token)}`;
}

/** Strip attribution/tracking parameters so canonical metadata stays clean. */
export function stripShareParams(url: string): string {
  try {
    const parsed = new URL(url, currentOrigin());
    [SHARE_TOKEN_PARAM, "ref", "referral", "utm_source", "utm_medium", "utm_campaign"].forEach((k) =>
      parsed.searchParams.delete(k),
    );
    const qs = parsed.searchParams.toString();
    return `${parsed.origin}${parsed.pathname}${qs ? `?${qs}` : ""}`;
  } catch {
    return url.split("?")[0];
  }
}

export function shareEntityLabel(type: string): string {
  return (SHARE_ENTITY_REGISTRY as Record<string, ShareEntityConfig>)[type]?.label ?? type;
}

export function isShareEntityType(value: string): value is ShareEntityType {
  return Object.prototype.hasOwnProperty.call(SHARE_ENTITY_REGISTRY, value);
}
